import React, { useEffect, useId, useRef } from 'react';
import { Mail, Phone, X } from 'lucide-react';
import { publicProspectPageConfig } from '../../config/publicProspectPageConfig';
import { resolveContactWidgetFocusAction } from './publicProspectContactWidgetFocus.js';

export { resolveContactWidgetFocusAction };

const CONTACT_WIDGET_POSITION =
  'fixed right-4 z-40 bottom-[calc(1rem+env(safe-area-inset-bottom))] sm:bottom-6';

export default function PublicProspectContactWidget({ open, onOpenChange, copy }) {
  const contact = publicProspectPageConfig.contact;
  const panelId = useId();
  const closeRef = useRef(null);
  const openRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const action = resolveContactWidgetFocusAction({ wasOpen: wasOpenRef.current, isOpen: open });
    wasOpenRef.current = open;
    if (action === 'focus-close') {
      closeRef.current?.focus();
    } else if (action === 'focus-open') {
      openRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  if (!open) {
    return (
      <div className={CONTACT_WIDGET_POSITION}>
        <button
          ref={openRef}
          type="button"
          aria-expanded="false"
          aria-controls={panelId}
          aria-label={copy.contactExpand}
          onClick={() => onOpenChange(true)}
          className="flex items-center gap-2 rounded-full border border-[#004747]/20 bg-white px-3 py-2 shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#004747] text-sm font-bold text-white">
            {contact.initials}
          </span>
          <span className="pr-1 text-sm font-semibold text-[#004747]">{contact.name.split(' ')[0]}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`${CONTACT_WIDGET_POSITION} w-[min(100vw-2rem,22rem)]`} id={panelId}>
      <div className="rounded-2xl border border-[#004747]/15 bg-white p-4 shadow-xl motion-safe:animate-none">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#004747] text-sm font-bold text-white">
              {contact.initials}
            </span>
            <div>
              <p className="prospect-display font-semibold text-[#0B1F2A]">{contact.name}</p>
              <p className="prospect-ui text-sm text-[#1f4a52]">{copy.contactQuestion}</p>
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label={copy.contactCollapse}
            onClick={() => onOpenChange(false)}
            className="rounded p-1 text-[#004747] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-2 text-sm text-[#1f4a52]">
          <p>
            <a className="underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]" href={contact.emailHref}>
              {contact.email}
            </a>
          </p>
          <p>
            <a className="underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]" href={contact.phoneHref}>
              {contact.phoneDisplay}
            </a>
          </p>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <a
            href={contact.emailHref}
            className="prospect-ui inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border border-[#004747] px-3 py-2 font-semibold text-[#004747] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            {copy.contactWrite}
          </a>
          <a
            href={contact.phoneHref}
            className="prospect-ui inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl bg-[#004747] px-3 py-2 font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {copy.contactCall}
          </a>
        </div>
      </div>
    </div>
  );
}
